import { api, ApiError } from '../lib/api.js';
import { el, render, fmtDate, fmtDateOnly, fmtRelative, roleLabel, memberStatusBadges, copy } from '../lib/dom.js';
import { canWrite } from '../lib/session.js';
import {
  icon,
  art,
  button,
  badge,
  roleChip,
  avatar,
  card,
  kpiCard,
  infoList,
  tabs,
  actionMenu,
  emptyState,
  errorState,
  fullRow,
  toast,
} from '../lib/ui.js';
import { accountActions, restoredPanel } from './account-actions.js';
import { membershipActions } from './membership-actions.js';

// ---------------------------------------------------------------------------
// User profile — one ConstructMind account across every organisation it
// belongs to. Read from GET /api/users/:userId (+ /activity).
//
// This page is framed by the ACCOUNT, not by an organisation, so it is one of
// the two places account-wide actions live (with the Users directory). The
// per-organisation verbs in the Organisations tab each name their org.
// ---------------------------------------------------------------------------

function accountStatusBadge(user) {
  if (user.accountDeletedAt) return badge('Account deleted', 'red', { dot: true });
  if (!user.accountActive) return badge('Account deactivated', 'red', { dot: true });
  if (!user.activated) return badge('Pending activation', 'blue', { dot: true });
  return badge('Active', 'green', { dot: true });
}

export function userDetailView({ userId, navigate, setCrumbs }) {
  const root = el('div', { class: 'stack' });
  const resultSlot = el('div', {});
  const tabsSlot = el('div', {});
  const panelSlot = el('div', {});
  const state = { detail: null, tab: 'overview' };
  let activity = null;

  function selectTab(id) {
    state.tab = id;
    renderTabs();
    renderPanel();
  }

  function renderTabs() {
    const d = state.detail;
    render(
      tabsSlot,
      tabs(
        [
          { id: 'overview', label: 'Overview' },
          { id: 'orgs', label: 'Organisations', count: d.memberships.length },
          { id: 'activity', label: 'Activity' },
        ],
        state.tab,
        selectTab,
      ),
    );
  }

  function renderPanel() {
    const d = state.detail;
    if (state.tab === 'orgs') render(panelSlot, membershipsTable(d));
    else if (state.tab === 'activity') {
      activity ??= activityPanel();
      render(panelSlot, card({ title: 'Activity', subtitle: 'Everything recorded for this account across every organisation, newest first.', body: activity.root }));
    } else render(panelSlot, overview(d));
  }

  function afterAccountAction(message, restored) {
    toast(message);
    if (restored) render(resultSlot, restoredPanel(restored));
    void reload();
  }

  function header(d) {
    const u = d.user;
    const actions = canWrite()
      ? accountActions({ ...u, userId: u.id, activeOrgCount: d.activeOrgCount }, { onDone: afterAccountAction })
      : [];
    const copyBtn = el(
      'button',
      {
        type: 'button',
        class: 'copy-btn',
        title: "Copy this user's internal ID, for support tickets and logs.",
        onclick: async (e) => {
          const ok = await copy(u.id);
          e.target.textContent = ok ? 'Copied' : 'Copy failed';
          setTimeout(() => (e.target.textContent = 'Copy'), 1800);
        },
      },
      'Copy',
    );

    return el(
      'div',
      { class: 'detail-head' },
      el('button', { type: 'button', class: 'back-link', onclick: () => navigate('/users') }, icon('arrow-left'), 'Back to Users'),
      card({
        body: el(
          'div',
          { class: 'detail-head__row' },
          el(
            'div',
            { class: 'person' },
            avatar(u.fullName, { size: 56 }),
            el(
              'div',
              {},
              el('div', { class: 'detail-head__title' }, el('h1', {}, u.fullName), accountStatusBadge(u), u.mfaEnabled ? el('span', { class: 'role-chip' }, 'MFA') : null, u.authProvider === 'AZURE_AD' ? el('span', { class: 'role-chip' }, 'SSO') : null),
              el('p', { class: 'detail-meta' }, u.email, el('span', { class: 'detail-meta__sep' }, '•'), 'User ID: ', el('strong', { class: 'mono' }, u.id), copyBtn),
            ),
          ),
          // Account-wide verbs as visible buttons: on this page there is no
          // organisation to mistake them for.
          actions.length > 0
            ? el(
                'div',
                { class: 'detail-head__actions' },
                actions.map((a) => button(a.label, { variant: a.danger ? 'danger-outline' : 'secondary', title: a.title, onClick: a.onSelect })),
              )
            : null,
        ),
      }),
    );
  }

  function kpis(d) {
    const u = d.user;
    return el(
      'div',
      { class: 'kpi-row' },
      kpiCard({ label: 'Organisations', value: String(d.activeOrgCount), iconName: 'kpi-building', tone: 'indigo', meta: d.memberships.length > d.activeOrgCount ? `${d.memberships.length - d.activeOrgCount} removed` : 'Live memberships' }),
      kpiCard({ label: 'Last login', value: u.lastLoginAt ? fmtRelative(u.lastLoginAt) : 'Never', iconName: 'log-out', tone: 'green', meta: u.lastLoginAt ? fmtDate(u.lastLoginAt) : 'Has not signed in yet' }),
      kpiCard({ label: 'Account created', value: fmtDateOnly(u.createdAt), iconName: 'kpi-users', tone: 'blue', meta: u.activated ? 'Onboarding complete' : 'Has not set a password yet' }),
      kpiCard({ label: 'Sign-in', value: u.authProvider === 'AZURE_AD' ? 'SSO' : 'Password', iconName: 'shield-alert', tone: 'amber', meta: u.mfaEnabled ? 'MFA enabled' : 'MFA not enabled' }),
    );
  }

  function overview(d) {
    const u = d.user;
    const orgName = (id) => d.memberships.find((m) => m.orgId === id)?.orgName ?? null;
    const orgLink = (id) => {
      const name = orgName(id);
      return id && name ? el('button', { type: 'button', class: 'link-btn', onclick: () => navigate(`/orgs/${id}`) }, name) : null;
    };
    return el(
      'div',
      { class: 'split' },
      card({
        title: 'User information',
        body: infoList([
          ['Full name', u.fullName],
          ['Email', u.email],
          ['Sign-in method', u.authProvider === 'AZURE_AD' ? 'Single sign-on (Azure AD)' : 'Password'],
          ['MFA', u.mfaEnabled ? badge('Enabled', 'green') : badge('Not enabled', 'gray')],
          ['Timezone', u.timezone],
        ]),
      }),
      card({
        title: 'Account information',
        body: infoList([
          ['Account status', accountStatusBadge(u)],
          ['Created', fmtDate(u.createdAt)],
          ['Password set', u.passwordChangedAt ? fmtDate(u.passwordChangedAt) : 'Not yet — invited'],
          ['Invite last sent', u.credentialsSentAt ? fmtDate(u.credentialsSentAt) : null],
          ['Home organisation', orgLink(u.homeOrgId)],
          ['Last active organisation', orgLink(u.lastActiveOrgId)],
        ]),
      }),
    );
  }

  function membershipsTable(d) {
    const rows = d.memberships.map((m) => {
      const items = [
        { label: `Open ${m.orgName}`, iconName: 'menu-view', onSelect: () => navigate(`/orgs/${m.orgId}?tab=members`) },
        ...(canWrite() && !m.orgDeleted
          ? membershipActions(m, {
              orgName: m.orgName,
              onChanged: (message) => {
                toast(message);
                void reload();
              },
              onCredentials: (node) => render(resultSlot, node),
            })
          : []),
      ];
      return el(
        'tr',
        {},
        el(
          'td',
          {},
          el(
            'div',
            { class: 'cell-main' },
            avatar(m.orgName, { square: true }),
            el(
              'div',
              { class: 'cell-main__text' },
              el('button', { type: 'button', class: 'link-btn cell-title', onclick: () => navigate(`/orgs/${m.orgId}`) }, m.orgName),
              m.orgDeleted ? el('span', { class: 'cell-sub' }, 'Organisation deleted') : !m.orgActive ? el('span', { class: 'cell-sub' }, 'Organisation inactive') : null,
            ),
          ),
        ),
        el('td', {}, roleChip(roleLabel(m.role))),
        el('td', {}, el('div', { class: 'cell-badges' }, memberStatusBadges(m))),
        el('td', { class: 'nowrap' }, fmtDateOnly(m.joinedAt)),
        el('td', { class: 'col-actions' }, actionMenu(items, { label: `Actions for ${m.orgName}` })),
      );
    });

    return el(
      'div',
      { class: 'table-card' },
      el(
        'p',
        { class: 'card-note' },
        'Each row is one organisation. These actions change this person’s access to that organisation only — use the buttons above for the whole account.',
      ),
      el(
        'div',
        { class: 'table-scroll' },
        el(
          'table',
          { class: 'table' },
          el('thead', {}, el('tr', {}, el('th', {}, 'Organisation'), el('th', {}, 'Role'), el('th', {}, 'Status'), el('th', {}, 'Joined'), el('th', { class: 'col-actions' }, ''))),
          el('tbody', {}, rows.length > 0 ? rows : fullRow(5, emptyState({ title: 'No memberships', text: 'This account does not belong to any organisation.', compact: true }))),
        ),
      ),
    );
  }

  function activityPanel() {
    const st = { page: 0, total: null, loading: false, entries: [] };
    const listSlot = el('div', { class: 'timeline-scroll' });
    const footerSlot = el('div', {});
    const PAGE = 25;

    function draw() {
      if (st.entries.length === 0) {
        render(listSlot, el('p', { class: 'section-hint' }, st.total === null ? 'Loading…' : 'No recorded activity for this account yet.'));
      } else {
        render(
          listSlot,
          el(
            'div',
            { class: 'timeline' },
            st.entries.map((a, i) =>
              el(
                'div',
                { class: 'timeline__item' },
                el('div', { class: 'timeline__node' }, art(i === st.entries.length - 1 && st.entries.length >= st.total ? 'timeline-node-last' : 'timeline-node')),
                el(
                  'div',
                  { class: 'timeline__details' },
                  el('p', { class: 'timeline__text' }, a.text),
                  el('p', { class: 'timeline__meta' }, fmtDate(a.createdAt), a.orgName ? [' · ', el('strong', {}, a.orgName)] : null),
                ),
              ),
            ),
          ),
        );
      }
      const hasMore = st.total !== null && st.entries.length < st.total;
      render(
        footerSlot,
        hasMore ? button(st.loading ? 'Loading…' : 'Load more', { size: 'sm', className: 'btn-block', disabled: st.loading, onClick: () => void next() }) : null,
      );
    }

    async function next() {
      if (st.loading) return;
      st.loading = true;
      draw();
      try {
        const page = st.page + 1;
        const data = await api.listUserActivity(userId, { page, pageSize: PAGE });
        st.page = page;
        st.total = data.total;
        st.entries = st.entries.concat(data.entries);
      } catch (err) {
        render(listSlot, el('p', { class: 'form-error' }, err instanceof ApiError ? err.message : 'Failed to load activity.'));
      } finally {
        st.loading = false;
      }
      draw();
    }

    draw();
    void next();
    return { root: el('div', { class: 'stack', style: 'gap:12px' }, listSlot, footerSlot) };
  }

  function renderAll(d) {
    setCrumbs([{ label: d.user.fullName }]);
    render(root, header(d), resultSlot, kpis(d), tabsSlot, panelSlot);
    renderTabs();
    renderPanel();
  }

  async function reload() {
    try {
      state.detail = await api.getUser(userId);
      renderAll(state.detail);
    } catch (err) {
      render(
        root,
        el('button', { type: 'button', class: 'back-link', onclick: () => navigate('/users') }, icon('arrow-left'), 'Back to Users'),
        card({
          body:
            err instanceof ApiError && err.status === 404
              ? emptyState({ title: 'User not found', text: 'The account may not exist, or the link is wrong.', action: button('Back to Users', { onClick: () => navigate('/users') }) })
              : errorState({ title: 'Failed to load this user', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void reload() }),
        }),
      );
    }
  }

  render(root, el('p', { class: 'section-hint' }, 'Loading…'));
  void reload();
  return root;
}
